import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { useDispatch } from 'react-redux'
import { changeMode, updateIsZooming } from 'features/map/mapSlice'
import { createMapItem, updateMapItem } from 'features/map/graphql/mutation'
import { addModal } from 'features/modals/modalSlice'
import { DrawItem } from '../types'
import { convertItemToSubmit, createPointForMobile, flattenCoords } from '../utils'
import { DrawImageMapPops } from './ControlsOverlay'
import EditMapItemForm from './EditMapItemForm'
import MapControls from './MapControls'
import imageMapCreator from '../lib/imagemap-creator'

interface DrawPops extends DrawImageMapPops {
    items: DrawItem[]
    callGetMapItems: () => void
    mapId: number
}

const S = {
    Wrapper: styled.div`
        display: flex;
        flex-direction: row;
    `,
    Controls: styled.div`
        position: fixed;
        right: 0;
        top: 50%;
        transform: translateY(-50%);
        z-index: 999;
    `,
}

const Draw = ({
    items,
    image,
    image_height,
    image_width,
    ignoreMouseClicks = false,
    callGetMapItems,
    mapId
}: DrawPops) => {
    const dispatch = useDispatch()
    const mapItems = items || []
    const idOfMapItems = mapItems.map((e) => e.generatedId)

    const [mapIsLoaded, setMapIsLoaded] = React.useState(false)
    const mapRef = React.useRef<
        undefined | ReturnType<typeof imageMapCreator>
    >()

    const handleCreateMapItem = useCallback(async (item) => {
        const createdItem = await createMapItem(convertItemToSubmit(item, mapId))
        await createMapItem(createPointForMobile(item, createdItem.data.createMapItem.id.toString(), mapId))
        await callGetMapItems()
        mapRef.current?.zoom?.(-10)
        mapRef.current?.setTool?.('cursor')
        dispatch(changeMode('viewing'))
        dispatch(updateIsZooming(false))
        dispatch(
            addModal({
                Component: ({ close }: { close: () => void }) => (
                    <EditMapItemForm
                        close={close}
                        drawItem={{ generatedId: createdItem.data.createMapItem?.id }}
                        callGetMapItems={callGetMapItems}
                    />
                ),
                size: 'lg',
                padding: 16,
            })
        )
    }, [callGetMapItems, dispatch, mapId])

    const imageOptions = useMemo(() => (
        {
            imageWidth: image_width,
            imageHeight: image_height,
            // Use this if you have cors issues local
            // image: `https://cors-anywhere.herokuapp.com/${image}`,
            image,
        }
    ), [image, image_height, image_width])

    const handleChangeCoords = (area: DrawItem) => {
        updateMapItem(area.generatedId, {
            coords: flattenCoords(area.coords)
        })
    }

    const handleZoomLogic = (isZooming: boolean) => {
        dispatch(updateIsZooming(isZooming))
    }

    // Load map
    React.useEffect(() => {
        if (!mapIsLoaded && mapId) {
            mapRef.current = imageMapCreator(
                'div-id',
                imageOptions,
                handleChangeCoords,
                (item) => {
                    handleCreateMapItem(item)
                },
                (scaledFactor) => {
                    handleZoomLogic(scaledFactor)
                }
            )
            setMapIsLoaded(true)
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mapIsLoaded, mapRef, imageOptions, idOfMapItems, handleCreateMapItem, mapId])

    // Sync ignoreMouseClicks to image map lib
    React.useEffect(() => {
        if (!mapIsLoaded) return
        mapRef.current?.setMouseClicksDisabled?.(ignoreMouseClicks)
    }, [mapIsLoaded, ignoreMouseClicks])

    // Sync areas to map
    React.useEffect(() => {
        if (mapRef.current) {
            mapRef.current.setItems(items)
        }
    }, [mapIsLoaded, items])

    return (
        <S.Wrapper>
            <S.Controls onClick={(e) => { e.stopPropagation(); e.preventDefault() }}>
                {mapIsLoaded && (
                    <MapControls {...mapRef.current} />
                )}
            </S.Controls>

            <div id="div-id" />
        </S.Wrapper>
    )
}

export default Draw
